import { createHttpClient } from 'mst-gql';
import { createClient, Client } from 'graphql-ws';
import axios, { AxiosError } from 'axios';
import Cookies from 'universal-cookie';
import * as Sentry from '@sentry/browser';
import { notification } from '@routific/hive';
import { rateLimitNotification } from '../../../notifications/rateLimit';

const cookies = new Cookies();

const WEBSOCKET_CLOSE_CODES = {
  NORMAL_CLOSURE: 1000,
  GOING_AWAY: 1001,
} as const;
// Codes 1000 and 1001 represent normal/expected closures (Normal Closure and Going Away respectively)
const isExpectedWebSocketClosure = (closeCode: number): boolean => {
  return closeCode === WEBSOCKET_CLOSE_CODES.NORMAL_CLOSURE || 
         closeCode === WEBSOCKET_CLOSE_CODES.GOING_AWAY;
};

export interface CreateConnectionOptions {
  apiUri: string;
  gqlUri: string;
  token?: string;
}

const getToken = (token?: string) => (token ? `Bearer ${token}` : '');

const createApiClient = ({ apiUri, token }: Pick<CreateConnectionOptions, 'apiUri' | 'token'>) => {
  const client = axios.create({ baseURL: apiUri });

  const setApiToken = (t: string) => {
    client.defaults.headers.common.Authorization = `Bearer ${t}`;
  };

  const clearApiToken = () => {
    client.defaults.headers.common.Authorization = undefined;
  };

  client.interceptors.response.use(undefined, (error: AxiosError) => {
    if (error.response?.status === 429) {
      notification(rateLimitNotification());
    }

    return Promise.reject(error);
  });

  if (token != null) {
    setApiToken(token);
  }

  return {
    api: client,
    setApiToken,
    clearApiToken,
  };
};

const createGqlClient = ({ gqlUri, token }: Pick<CreateConnectionOptions, 'gqlUri' | 'token'>) => {
  const isSecureUrl = gqlUri.includes('https');
  const wsBase = isSecureUrl ? 'wss://' : 'ws://';
  const wsUri = `${gqlUri.replace(/(^http:\/\/)|(^https:\/\/)/gi, wsBase)}`;

  const gqlHttpClient = createHttpClient(gqlUri, {
    headers: {
      Authorization: getToken(token),
      'x-routific-client': 'WEB',
    },
  });

  let gqlWsClient: Client;
  try {
    gqlWsClient = createClient({
      url: wsUri,
      /**
       * shouldRetry controls whether the client should retry connection failures.
       *
       * Retry triggers on:
       * - Non-fatal WebSocket close codes (1000, 1001, 1005, 1006, 1012-1014)
       * - Network disconnections (AbortError, connection timeout, etc.)
       * - Server unavailability errors
       *
       * Does NOT retry on fatal close codes (always fails immediately):
       * - 4500: Internal Server Error
       * - 4005: Internal Client Error
       * - 4400: Bad Request
       * - 4004: Bad Response
       * - 4401: Unauthorized
       * - 4406: Subprotocol Not Acceptable
       * - 4409: Subscriber Already Exists
       * - 4429: Too Many Initialisation Requests
       * - Other codes in 4000-4999 range (application-specific errors)
       *
       * Setting to `() => true` ensures persistent reconnection for transient failures
       * like server restarts, network issues, or load balancer changes, while still
       * failing immediately on auth/protocol errors that won't resolve with retries.
       */
      shouldRetry: () => true,
      retryAttempts: 721, // ~1 hour of retry attempts with exponential backoff capped at 5s
      retryWait: async (retries) => {
        // Exponential backoff: 2^retries seconds, capped at 5 seconds
        const exponentialDelay = Math.min(Math.pow(2, retries) * 1000, 5000);
        await new Promise(resolve => setTimeout(resolve, exponentialDelay));
      },
      connectionParams: () => ({ authorization: getToken(cookies.get('access_token')) }),
      on: {
        error: (error) => {
        // Capture WebSocket connection errors
          Sentry.captureException(error, {
            tags: {
              component: 'graphql-ws',
              errorType: 'websocket_error',
            },
            extra: {
              wsUri,
              message: error instanceof Error ? error.message : 'WebSocket connection error',
            },
          });
        },
        closed: (event: unknown) => {
          // Capture unexpected closures (not initiated by client)
          const closeEvent = event as CloseEvent;
          if (closeEvent && !isExpectedWebSocketClosure(closeEvent.code)) {
            Sentry.captureMessage('WebSocket connection closed unexpectedly', {
              level: 'error',
              tags: {
                component: 'graphql-ws',
                errorType: 'websocket_closed',
              },
              extra: {
                wsUri,
                code: closeEvent.code,
                reason: closeEvent.reason,
                wasClean: closeEvent.wasClean,
              },
            });
            console.error('WebSocket closed unexpectedly:', { code: closeEvent.code, reason: closeEvent.reason });
          }
        },
        connecting: () => {
          console.log('WebSocket connecting to:', wsUri);
        },
        connected: () => {
          console.log('WebSocket connected successfully');
        },
      },
    });
  } catch (error) {
    // Capture errors during WebSocket client initialization
    Sentry.captureException(error, {
      tags: {
        component: 'graphql-ws',
        errorType: 'websocket_initialization_failed',
      },
      extra: {
        wsUri,
        gqlUri,
        message: error instanceof Error ? error.message : 'Failed to initialize WebSocket client',
      },
    });
    console.error('Failed to initialize WebSocket client:', error);
    throw error; // Re-throw to maintain existing error handling
  }

  return {
    gqlHttpClient,
    gqlWsClient,
    setGqlToken: (t: string) => {
      gqlHttpClient.setHeader('Authorization', getToken(t));
    },
    clearGqlToken: () => {
      gqlHttpClient.setHeader('Authorization', '');
    },
    closeGqlConnection: () => {
      gqlWsClient.dispose();
    },
  };
};

const createConnection = ({ apiUri, gqlUri, token }: CreateConnectionOptions) => {
  const { api, ...apiClient } = createApiClient({ apiUri, token });
  const { gqlHttpClient, gqlWsClient, ...gqlClient } = createGqlClient({ gqlUri, token });

  return {
    api,
    gqlHttpClient,
    // "graphqlWsClient" tells mst-gql to use graphql-ws
    graphqlWsClient: gqlWsClient,
    setToken: (t: string) => {
      cookies.set('access_token', t, { secure: true, path: '/' });
      apiClient.setApiToken(t);
      gqlClient.setGqlToken(t);
    },
    clearToken: () => {
      cookies.remove('access_token', { path: '/' });
      cookies.remove('application_token', { path: '/' });
      // Remove token if created by single sign-on from full-product (v2)
      cookies.remove('access_token', { domain: '.routific.com', path: '/' });
      apiClient.clearApiToken();
      gqlClient.clearGqlToken();
      gqlClient.closeGqlConnection();
    },
  };
};

export type Connection = ReturnType<typeof createConnection>;

export default createConnection;
